/**
 * 反馈工具函数
 * 用于管理用户对功能的期待反馈
 */

const FEEDBACK_STORAGE_KEY = 'imgcloud_feature_feedback';
const FEEDBACK_API_URL = '/api/image-processing/feedback';

export interface FeedbackData {
  [toolKey: string]: {
    isExpected: boolean;
    timestamp?: number;
  };
}

/**
 * 获取本地存储的反馈数据
 */
export const getLocalFeedback = (): FeedbackData => {
  try {
    const stored = localStorage.getItem(FEEDBACK_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('获取反馈数据失败:', error);
    return {};
  }
};

/**
 * 保存反馈到本地存储
 */
export const saveLocalFeedback = (toolKey: string, isExpected: boolean): void => {
  try {
    const feedback = getLocalFeedback();
    feedback[toolKey] = {
      isExpected,
      timestamp: Date.now(),
    };
    localStorage.setItem(FEEDBACK_STORAGE_KEY, JSON.stringify(feedback));
  } catch (error) {
    console.error('保存反馈数据失败:', error);
  }
};

/**
 * 检查用户是否期待某个功能
 */
export const isFeatureExpected = (toolKey: string): boolean => {
  const feedback = getLocalFeedback();
  return feedback[toolKey]?.isExpected || false;
};

/**
 * 提交反馈到后端（用于统计真实期待人数）
 */
export const submitFeedbackToServer = async (
  toolKey: string,
  isExpected: boolean
): Promise<{ success: boolean; count?: number; message?: string }> => {
  try {
    const uid = localStorage.getItem('fdd_uid') || 'anonymous';
    
    const response = await fetch(FEEDBACK_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        toolKey,
        isExpected,
        uid,
      }),
    });

    if (!response.ok) {
      throw new Error(`请求失败: ${response.status}`);
    }

    const data = await response.json();
    
    if (data.success) {
      return {
        success: true,
        count: data.count,
        message: data.message,
      };
    } else {
      throw new Error(data.message || '提交失败');
    }
  } catch (error) {
    console.error('提交反馈到服务器失败:', error);
    // 失败时不影响本地存储
    return {
      success: false,
      message: error instanceof Error ? error.message : '网络错误，已保存到本地',
    };
  }
};

/**
 * 获取功能的期待人数（从服务器）
 */
export const getFeatureExpectedCount = async (
  toolKey: string
): Promise<number> => {
  try {
    const response = await fetch(`${FEEDBACK_API_URL}/count?toolKey=${encodeURIComponent(toolKey)}`);
    if (!response.ok) {
      throw new Error(`请求失败: ${response.status}`);
    }
    const data = await response.json();
    if (data.success) {
      return data.count || 0;
    } else {
      throw new Error(data.message || '获取失败');
    }
  } catch (error) {
    console.error('获取期待人数失败:', error);
    return 0;
  }
};

/**
 * 获取所有功能的期待人数（从服务器）
 */
export const getAllFeatureExpectedCounts = async (): Promise<Record<string, number>> => {
  try {
    const response = await fetch(`${FEEDBACK_API_URL}/counts`);
    if (!response.ok) {
      throw new Error(`请求失败: ${response.status}`);
    }
    const data = await response.json();
    if (data.success) {
      return data.counts || {};
    } else {
      throw new Error(data.message || '获取失败');
    }
  } catch (error) {
    console.error('获取所有期待人数失败:', error);
    return {};
  }
};

